import React, { useState, useEffect, useRef, useCallback } from 'react';
import { TechnicianCard } from '../components/TechnicianCard';
import { Button } from '../components/Button';
import './TechnicianTrackingPage.css';

interface Technician {
  id: string;
  name: string;
  rating: number;
  eta: string;
  phoneNumber: string;
  vehicleInfo: string;
  location: {
    latitude: number;
    longitude: number;
  };
}

// Extend the Window interface to include google
declare global {
  interface Window {
    google: any;
    initMap: () => void;
    googleMapsLoaded: boolean;
    initGoogleMaps: () => void;
  }
}

export const TechnicianTrackingPage: React.FC = () => {
  const [technician, setTechnician] = useState<Technician | null>(null);
  const [userLocation] = useState({
    latitude: 37.78825,
    longitude: -122.4324,
  });
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const userMarkerRef = useRef<any>(null);
  const technicianMarkerRef = useRef<any>(null);

  // Initialize Google Map
  const initMap = useCallback(() => {
    if (!mapRef.current || !window.google || !window.google.maps || !window.google.maps.LatLng) {
      console.log('Google Maps not fully loaded yet');
      return;
    }

    const userPos = new window.google.maps.LatLng(userLocation.latitude, userLocation.longitude);
    
    mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
      center: userPos,
      zoom: 15,
      styles: [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }]
        }
      ]
    });

        // Add user location marker using regular Marker
        userMarkerRef.current = new window.google.maps.Marker({
          position: userPos,
          map: mapInstanceRef.current,
          title: 'Your Location',
          icon: {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" fill="#2196F3" stroke="white" stroke-width="2"/>
                <text x="12" y="16" text-anchor="middle" fill="white" font-size="12" font-weight="bold">U</text>
              </svg>
            `),
            scaledSize: new window.google.maps.Size(24, 24),
            anchor: new window.google.maps.Point(12, 12)
          }
        });

    // Add info window for user location
    const userInfoWindow = new window.google.maps.InfoWindow({
      content: '<div style="padding: 8px;"><strong>Your Location</strong><br/>You are here</div>'
    });

    userMarkerRef.current.addListener('click', () => {
      userInfoWindow.open(mapInstanceRef.current, userMarkerRef.current);
    });
  }, [userLocation]);

  // Update technician marker when technician data changes
  const updateTechnicianMarker = useCallback((tech: Technician) => {
    if (!mapInstanceRef.current || !window.google || !window.google.maps || !window.google.maps.LatLng) return;

    const techPos = new window.google.maps.LatLng(tech.location.latitude, tech.location.longitude);
    const userPos = new window.google.maps.LatLng(userLocation.latitude, userLocation.longitude);
    
    // Remove existing technician marker
    if (technicianMarkerRef.current) {
      technicianMarkerRef.current.setMap(null);
    }

        // Add new technician marker using regular Marker
        technicianMarkerRef.current = new window.google.maps.Marker({
          position: techPos,
          map: mapInstanceRef.current,
          title: tech.name,
          icon: {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" fill="#4CAF50" stroke="white" stroke-width="2"/>
                <text x="12" y="16" text-anchor="middle" fill="white" font-size="12" font-weight="bold">T</text>
              </svg>
            `),
            scaledSize: new window.google.maps.Size(24, 24),
            anchor: new window.google.maps.Point(12, 12)
          }
        });

    // Add info window for technician
    const techInfoWindow = new window.google.maps.InfoWindow({
      content: `<div style="padding: 8px;"><strong>${tech.name}</strong><br/>ETA: ${tech.eta}<br/>${tech.vehicleInfo}</div>`
    });

    technicianMarkerRef.current.addListener('click', () => {
      techInfoWindow.open(mapInstanceRef.current, technicianMarkerRef.current);
    });

    // Fit map to show both markers
    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend(userPos);
    bounds.extend(techPos);
    mapInstanceRef.current.fitBounds(bounds);
  }, [userLocation]);

  useEffect(() => {
    // Initialize map when component mounts
    const checkGoogleMaps = () => {
      if (window.googleMapsLoaded && window.google && window.google.maps && window.google.maps.LatLng && window.google.maps.Map) {
        try {
          initMap();
        } catch (error) {
          console.error('Google Maps initialization error:', error);
          // Show fallback message if API key is restricted
          if (mapRef.current) {
            mapRef.current.innerHTML = `
              <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; background: #f5f5f5; color: #666; text-align: center; padding: 20px;">
                <i class="fas fa-map-marked-alt" style="font-size: 48px; color: #2196F3; margin-bottom: 16px;"></i>
                <h3 style="margin: 0 0 8px 0; color: #333;">Map Loading Issue</h3>
                <p style="margin: 0; font-size: 14px;">Please configure your Google Maps API key to allow localhost access.</p>
                <p style="margin: 8px 0 0 0; font-size: 12px; color: #999;">Add "localhost:*" to your API key restrictions in Google Cloud Console.</p>
              </div>
            `;
          }
        }
      } else {
        // Retry after 200ms if Google Maps isn't loaded yet
        setTimeout(checkGoogleMaps, 200);
      }
    };

    // Check if Google Maps is already loaded, otherwise wait for callback
    if (window.googleMapsLoaded) {
      checkGoogleMaps();
    } else {
      // Set up a listener for when Google Maps loads
      const originalCallback = window.initGoogleMaps;
      window.initGoogleMaps = function() {
        if (originalCallback) originalCallback();
        checkGoogleMaps();
      };
    }

    // Simulate loading technician data
    const mockTechnician: Technician = {
      id: '1',
      name: 'Mike Johnson',
      rating: 4.8,
      eta: '15 min',
      phoneNumber: '(555) 987-6543',
      vehicleInfo: 'White Ford F-150',
      location: {
        latitude: 37.78425,
        longitude: -122.4284,
      },
    };

    // Simulate API call delay
    setTimeout(() => {
      setTechnician(mockTechnician);
    }, 1000);
  }, [initMap]);

  // Update technician marker when technician state changes
  useEffect(() => {
    if (technician) {
      updateTechnicianMarker(technician);
    }
  }, [technician, updateTechnicianMarker]);

  const handleCallTechnician = () => {
    if (technician) {
      if (window.confirm(`Call ${technician.name} at ${technician.phoneNumber}?`)) {
        console.log('Calling technician...');
      }
    }
  };

  const handleCancelRequest = () => {
    if (window.confirm('Are you sure you want to cancel this service request?')) {
      setTechnician(null);
      // In a real app, you might navigate back to home
      console.log('Request cancelled');
    }
  };

  return (
    <div className="tracking-page">
      <div className="tracking-header">
        <h1 className="tracking-title">Technician Tracking</h1>
        <p className="tracking-subtitle">Track your technician in real-time</p>
      </div>

      <div className="map-container">
        <div ref={mapRef} className="google-map"></div>
      </div>

      {technician ? (
        <div className="technician-info">
          <TechnicianCard
            name={technician.name}
            rating={technician.rating}
            eta={technician.eta}
            phoneNumber={technician.phoneNumber}
            vehicleInfo={technician.vehicleInfo}
          />
          
          <div className="action-buttons">
            <Button
              title="Call Technician"
              onPress={handleCallTechnician}
              variant="primary"
              style={{ flex: 1, marginRight: '6px' }}
            />
            <Button
              title="Cancel Request"
              onPress={handleCancelRequest}
              variant="outline"
              style={{ flex: 1, marginLeft: '6px' }}
            />
          </div>
        </div>
      ) : (
        <div className="loading-container">
          <div className="loading-spinner">
            <i className="fas fa-spinner fa-spin"></i>
          </div>
          <h3 className="loading-text">Finding a technician...</h3>
          <p className="loading-subtext">
            We're matching you with the nearest available technician
          </p>
        </div>
      )}
    </div>
  );
};
